import { ThemeProvider } from "@material-ui/core/styles"
import * as Sentry from "@sentry/browser"
import emotionTheme from "@tdc-core/theme/default"
import { ThemeProvider as EmotionThemeProvider } from "emotion-theming"
import withReduxSaga from "next-redux-saga"
import withRedux from "next-redux-wrapper"
import App from "next/app"
import "moment/locale/zh-tw"
import Router from "next/router"
import React from "react"
import { connect, Provider } from "react-redux"
import { requestID } from "states/apiServices"
import { changeLocale } from "utils/commonUtils"
import messages from "utils/validation/messages"
import FormProvider from "../components/form-provider"
import Header from "../components/header"
import FooterDivider from "../components/layouts/footer-divider"

import LoadingSpinner from "../components/modals/loading-spinner"
import createStore from "../states/store"
import loadCSS from "../utils/loadCSS"
import Config from "../utils/publicRuntimeConfig"
import { UserEmail as extractUserEmail } from "../utils/selector"
import "../styles/styles.css"
import theme from "../utils/theme"
import rules from "../utils/validation/rules"
import "moment/locale/zh-cn"
import Head from "next/head"

if (Config.sentryDSN) {
  Sentry.init({
    dsn: Config.sentryDSN,
    enabled: true,
    release: Config.buildId || process.env.CI_COMMIT_SHORT_SHA,
    debug: Config.appENV === "prod" || Config.appENV === "uat" || Config.appENV === "sit" || Config.appENV === "dev" || Config.appENV === "dr",
    tracesSampleRate: 1.0
  })

  Sentry.configureScope((scope) => {
    Config.appENV && scope.setTag("environment", Config.appENV)
    Config.buildId && scope.setTag("release", Config.buildId)
    process.env.CI_COMMIT_SHORT_SHA && scope.setTag("ci-commit-short-sha", process.env.CI_COMMIT_SHORT_SHA)
    scope.setTag("x-request-id", requestID)
  })
}

class MyApp extends App {
  static async getInitialProps({ Component, ctx }) {
    let pageProps = {}
    if (Component.getInitialProps) {
      pageProps = await Component.getInitialProps({
        ctx
      })
    }
    if (ctx.isServer) {
      console.log("[MyApp] server rendering end")
    }

    return {
      pageProps
    }
  }

  constructor(props, context) {
    super(props, context)

    this.state = {
      showLoadingSpinner: false
    }
  }

  handleShowLoadingModal = () => {
    this.setState({ showLoadingSpinner: true })
  }

  handleCloseLoadingModal = () => {
    this.setState({ showLoadingSpinner: false })
  }

  componentDidMount() {
    const head = document.head || document.getElementsByTagName("head")[0]
    loadCSS({
      id: "cookieconsent_min_css",
      targetElement: head,
      href: `https://${Config.domain}.hktdc.com/resources/cookieconsent/css/cookieconsent.min.css`
    })
    loadCSS({
      id: "cookieconsent_hktdc_css",
      targetElement: head,
      href: `https://${Config.domain}.hktdc.com/resources/cookieconsent/css/cookieconsent.hktdc.css`
    })
    //Fires when a route starts to change
    Router.events.on("routeChangeStart", (url) => {
      this.handleShowLoadingModal()
      console.log("routeChangeStart url: ", url)
    })
    //Fires when a route changed completely
    Router.events.on("routeChangeComplete", (url) => {
      this.handleCloseLoadingModal()
      window.scroll({
        top: 0,
        left: 0
      })
    })
    //Fires when there's an error when changing routes, or a route load is cancelled
    Router.events.on("routeChangeError", (err, url) => {
      this.handleCloseLoadingModal()
      console.log("routeChangeError err: ", err, " url: ", url)
    })

    const query = this.props.pageProps.query
    const isPaymentReturnUrl = ["/basic/failure/[lang]", "/payment-success/[lang]"].includes(Router.pathname)
    changeLocale(query?.lang, isPaymentReturnUrl)
  }

  componentDidCatch(error, errorInfo) {
    const { companyEmail } = this.props
    Sentry.configureScope(function (scope) {
      companyEmail && scope.setUser({ email: companyEmail })
      Object.keys(errorInfo).forEach((key) => {
        scope.setExtra(key, errorInfo[key])
      })
    })
    Sentry.captureException(error)
    super.componentDidCatch(error, errorInfo)
  }

  render() {
    const { Component, pageProps, store } = this.props

    return (
      <>
        <Head>
          <meta name="viewport"
                content="minimum-scale=1, initial-scale=1, width=device-width, shrink-to-fit=no, maximum-scale=1, user-scalable=no" />
        </Head>
        <Provider store={store}>
          <FormProvider rules={rules} messages={messages}>
            <EmotionThemeProvider theme={emotionTheme}>
            <ThemeProvider theme={theme}>
                <Header />
                <Component {...pageProps} />
                {this.state.showLoadingSpinner ? <LoadingSpinner /> : null}
                <FooterDivider />
              </ThemeProvider>
            </EmotionThemeProvider>
          </FormProvider>
        </Provider>
      </>
    )
  }
}

const mapStateToProps = (state) => ({
  companyEmail: extractUserEmail(state)
})

const mapDispatchToProps = (dispatch) => ({})

const connectedMyApp = connect(mapStateToProps, mapDispatchToProps)(MyApp)

export default withRedux(createStore)(withReduxSaga(connectedMyApp))
